/**
 * The rules for an agent's name — one place, used by every path that accepts
 * one (hire, discovery start/complete/claim, skip, the availability probe).
 *
 * Two values come out of a user's input, and they are not the same thing:
 *
 *   - the **display name** (`instances.bot_display_name`) — the label, shown
 *     everywhere. Keeps the user's own spelling, diacritics and script.
 *   - the **email token** (`instances.email_token`) — the identity. ASCII-only,
 *     because it is the local part of the agent's email address, its URL slug
 *     (`/a/<token>`) and the key its deployed apps are served under
 *     (`{email_token}--{slug}`). Derived once at hire and then frozen.
 *
 * Folding the display name down to the token is lossy on purpose. What matters
 * is that it is lossy in a way a Swedish user recognises: "Björn" must become
 * `bjorn`, not `bj-rn` (which is what six live agents are stuck with).
 */

import { stripLoneSurrogates, truncateChars } from "@shared/text";

/**
 * Max length of a user-chosen display name.
 *
 * The name is a chat header, a sidebar entry, an email From line and a URL, so
 * it has to stay readable as all four. 24 fits Swedish compounds ("Min
 * mäklarassistent" is 19) while ruling out sentence-length names. Enforced
 * server-side by `normalizeBotDisplayName`; an input's `maxLength` is only a
 * hint that stops the field growing past it as you type.
 */
export const MAX_BOT_NAME_LENGTH = 24;

/**
 * Latin letters carrying no combining mark for NFD to separate, so the
 * decompose-and-strip pass below leaves them untouched. Lowercase keys only —
 * folding runs after `toLowerCase()`.
 */
const LATIN_LIGATURES: Record<string, string> = {
  "ß": "ss",
  "æ": "ae",
  "œ": "oe",
  "ø": "o",
  "đ": "d",
  "ð": "d",
  "þ": "th",
  "ł": "l",
  "ħ": "h",
  "ŋ": "n",
};

/**
 * Latin text with its diacritics removed: "björn" → "bjorn", "straße" →
 * "strasse". NFD splits a letter into base + combining mark so the marks can be
 * dropped, which also handles the awkward ones — Turkish "İ" lowercases to "i"
 * plus a combining dot, and that dot is a mark like any other.
 *
 * Non-Latin scripts have nothing to decompose and pass through unchanged;
 * `botNameToEmailToken` is what discards them.
 *
 * Not exported: the ligature map is lowercase-keyed, so this is only correct on
 * input that has already been lowercased — which its one caller does.
 */
const foldToAscii = (text: string): string =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\u0000-\u007f]/g, (char) => LATIN_LIGATURES[char] ?? char);

/**
 * FNV-1a over the name. Not a security hash — it only has to be stable and
 * spread evenly, and it must run in the browser (this module is imported by
 * client components for MAX_BOT_NAME_LENGTH, so `node:crypto` is out).
 */
const nameHash = (text: string): string => {
  // Over UTF-16 code units deliberately: a hash only has to be stable and well
  // spread, and code units make that explicit without decomposing anything.
  const h = Array.from({ length: text.length }, (_, i) => text.charCodeAt(i))
    .reduce((acc, code) => Math.imul(acc ^ code, 0x01000193) >>> 0, 0x811c9dc5);
  return h.toString(16).padStart(8, "0");
};

/**
 * The email-token form of a display name. Total: every non-empty name yields a
 * token, so callers never have to handle "" as a special case.
 *
 * A name that folds to no ASCII at all (李明, Ольга, an emoji) gets a token
 * derived from a hash of the name rather than from its letters. That token is
 * still a FUNCTION OF THE NAME, which is what makes it safe to mint here: the
 * availability checks in agents-db all key on the token, so a randomly minted
 * one would silently match nothing — the same name would be "available" twice
 * over, no restriction row could ever apply to it, and a discovery reservation
 * would reserve a token the eventual hire never claims. Deriving it keeps
 * "same name, same token" true for every script.
 *
 * `orgSlug` appends the organization's namespace: "Nora" + "foo" → `nora.foo`.
 * The dot is SYSTEM-INSERTED and unreachable from the name half — the filter
 * below maps `.` to `-`, so a user cannot claim `nora.foo` by naming an agent
 * "Nora.Foo". That is what keeps the org namespace disjoint from the global
 * one, and why an org suffix can be added without any collision migration:
 * no token minted before this existed contains a dot.
 */
export function botNameToEmailToken(botDisplayName: string, orgSlug?: string | null): string {
  const base = baseEmailToken(botDisplayName);
  if (!base) return "";
  if (!orgSlug) return base;
  // The whole token is an email local part, and RFC 5321 caps that at 64
  // octets. The name half can reach 48 (24 characters, each folding to two
  // ASCII through the ligature map), so a long slug can push the sum past it —
  // and the result is not a validation error anywhere, it is an agent with an
  // address no MTA will accept. The slug is the half that must survive intact:
  // it is the organization's identity and is shared by every agent in it.
  const budget = EMAIL_LOCAL_MAX_OCTETS - 1 - orgSlug.length;
  return `${clipToken(base, budget)}.${orgSlug}`;
}

/** RFC 5321 §4.5.3.1.1 — the maximum length of an email address's local part. */
const EMAIL_LOCAL_MAX_OCTETS = 64;

/** Clip a token half to `max`, without leaving the separator hyphen exposed at
 *  the cut. Tokens are ASCII by construction here, so bytes and characters are
 *  the same thing and `slice` is safe. */
function clipToken(token: string, max: number): string {
  return max >= token.length ? token : token.slice(0, Math.max(0, max)).replace(/-+$/, "");
}

/** The name half of a token, before any org suffix. */
function baseEmailToken(botDisplayName: string): string {
  const folded = foldToAscii(botDisplayName.toLowerCase())
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  if (folded) return folded;
  const normalised = normalizeBotDisplayName(botDisplayName);
  return normalised ? `agent-${nameHash(normalised)}` : "";
}

/**
 * The stored form of a display name: control characters removed (they reach an
 * email From header otherwise), internal whitespace runs collapsed, trimmed,
 * and clipped to MAX_BOT_NAME_LENGTH without splitting a surrogate pair.
 * Returns "" when nothing usable is left.
 */
export function normalizeBotDisplayName(raw: string): string {
  // stripLoneSurrogates as well as truncateChars: the clip only guarantees its
  // OWN cut is well-formed, and a broken pair can arrive in the request body.
  // The name reaches a jsonb column (channels.config.agent_name), where a lone
  // surrogate is a 22P02 that aborts the insert — the house rule in
  // @shared/text exists for exactly this.
  const printable = stripLoneSurrogates(raw).replace(/[\u0000-\u001f\u007f-\u009f]/g, " ");
  return truncateChars(printable.replace(/\s+/g, " ").trim(), MAX_BOT_NAME_LENGTH).trim();
}
